import "./App.css";
import logo from "./images/highResLogo.png";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";

import Home from "./home";
import { KhushiItinerary } from "./Khushi";
import Jarvis from "./Jarvis";
import NavBar from "./components/navbar";
import GeneralDash from "./GeneralDash";
import { Box } from "@mui/material";
import { useState, useEffect, useRef, type ReactNode } from "react";
import TermsOfService from "./TermsOfService";
import { isOwnerEmail } from "./ownerConfig";
import firebase, { db } from "./firebase";
import { AppColors } from "./theme/colors";
const whitelist_default = [
  "ayush@acchaudhary.com",
  "cordi.campbell@gmail.com",
  "ayushi@acchaudhary.com",
]; // Whitelist of allowed emails

const RESUME_FALLBACK_URL = "/files/Resume.pdf";
const RESUME_PAGE_TITLE = "Ayush Chaudhary — Resume";

function ResumePage() {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = RESUME_PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      try {
        const snap = await db.collection("site_config").doc("resume").get();
        const url = snap.exists ? (snap.data()?.activeDownloadUrl as unknown) : null;
        if (!cancelled && typeof url === "string" && url.trim().length > 0) {
          setResolvedUrl(url);
          return;
        }
      } catch {
        // fall through to default
      }
      if (!cancelled) setResolvedUrl(RESUME_FALLBACK_URL);
    }
    void resolve();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!resolvedUrl) {
    return (
      <div className="App-header">
        <img className="logo" src={logo} alt="ACC Logo" />
        <br />
        <div
          style={{
            border: "2px solid red",
            padding: "0px 50px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <a style={{ fontSize: 120, fontStyle: "thin" }}>Loading . . .</a>
          </div>
        </div>
      </div>
    );
  }

  // PDF viewer hash params keep the toolbar visible (so users can download from
  // the embedded viewer) while hiding the side panel and fitting page width.
  const iframeSrc = `${resolvedUrl}#toolbar=1&navpanes=0&view=FitH`;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: AppColors.appBg,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 2,
          display: "flex",
          gap: 1,
        }}
      >
        <a
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: "0.85rem",
            padding: "6px 12px",
            borderRadius: 6,
            background: "rgba(0, 0, 0, 0.55)",
            color: "#ffffff",
            textDecoration: "none",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(4px)",
          }}
        >
          Open in new tab
        </a>
      </Box>
      <iframe
        src={iframeSrc}
        title={RESUME_PAGE_TITLE}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
        }}
      />
    </Box>
  );
}

function OwnerPrivateRoute({
  children,
  alt,
  authentication,
  isOwner,
}: {
  children: ReactNode;
  alt: ReactNode;
  authentication: boolean;
  isOwner: boolean;
}) {
  return authentication ? (
    isOwner ? (
      children
    ) : (
      alt
    )
  ) : (
    <Navigate to="/home" replace />
  );
}

function AppRoutes({
  authentication,
  isOwner,
}: {
  authentication: boolean;
  isOwner: boolean;
}) {
  const location = useLocation();
  const hideNav = location.pathname === "/khushi";

  return (
    <Box>
      {!hideNav && <NavBar auth={authentication} isOwner={isOwner} />}
      <Box
        minWidth="100%"
        minHeight="100vh"
        bgcolor={hideNav ? AppColors.khushiPageBg : AppColors.appBg}
      >
        <Routes>
          {/* This route is for home component 
              with exact path "/", in component props 
              we passes the imported component*/}
          <Route path="/home" element={<Home auth={authentication} />} />

          {/* This route is for home component 
              with exact path "/", in component props 
              we passes the imported component*/}
          <Route
            path="/jarvis"
            element={
              <OwnerPrivateRoute
                authentication={authentication}
                isOwner={isOwner}
                alt={<GeneralDash />}
              >
                <Jarvis />
              </OwnerPrivateRoute>
            }
          />

          <Route path="/general" element={<GeneralDash />} />
          <Route path="/termsOfService" element={<TermsOfService />} />
          <Route path="/khushi" element={<KhushiItinerary />} />

          {/* If any route mismatches the upper 
              route endpoints then, redirect triggers 
              and redirects app to home component with to="/" */}
          <Route path="/" element={<Navigate replace to="/home" />} />
        </Routes>
      </Box>
    </Box>
  );
}

function AppShell() {
  const [authentication, setAuthState] = useState(true);
  /** Loaded allowlist: Firestore in production, or `whitelist_default` in local dev / on fetch error. */
  const [allowlistEmails, setAllowlistEmails] = useState<string[] | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const allowlistInitRef = useRef(false);

  useEffect(() => {
    if (allowlistInitRef.current) return;
    allowlistInitRef.current = true;

    async function loadUserAllowlist() {
      const isDeployedBuild = process.env.NODE_ENV === "production";

      // `yarn dev` / `yarn start`: development — no Firestore read; use bundled backup only.
      // `yarn build` + deploy: production — Firestore `users` first, then `whitelist_default` on error.
      if (!isDeployedBuild) {
        setAllowlistEmails(whitelist_default);
        setIsReady(true);
        return;
      }

      const users = db.collection("users");
      try {
        const usersSnapshot = await users.get();
        console.log("db read");
        const emails = usersSnapshot.docs
          .map((doc) => doc.data()["email"])
          .filter(Boolean) as string[];
        setAllowlistEmails(emails);
      } catch (error) {
        alert(
          "Database access error (see console)... reverting to local whitelist backup",
        );
        console.log("error", error);
        setAllowlistEmails(whitelist_default);
      }
      setIsReady(true);
    }

    loadUserAllowlist();
  }, []);

  useEffect(() => {
    if (allowlistEmails && process.env.NODE_ENV === "development") {
      console.debug("[allowlist]", allowlistEmails);
    }
  }, [allowlistEmails]);

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged((user) => {
        if (user) {
          setAuthState(true);
          setIsOwner(isOwnerEmail(user.email));
        } else {
          setAuthState(false);
          setIsOwner(false);
        }
      }),
    [],
  );

  if (!isReady) {
    return (
      <div className="App-header">
        <img className="logo" src={logo} alt="ACC Logo" />
        <br />
        <div
          style={{
            border: "2px solid red",
            padding: "0px 50px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <a style={{ fontSize: 120, fontStyle: "thin" }}>Loading . . .</a>
          </div>
        </div>
      </div>
    );
  } else {
    return <AppRoutes authentication={authentication} isOwner={isOwner} />;
  }
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/resume" element={<ResumePage />} />
        <Route path="*" element={<AppShell />} />
      </Routes>
    </Router>
  );
}

export default App;
